import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const label = "block font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] mb-2";

const STATUS_LABELS = { hold: "PENDING", paid: "PAID", failed: "FAILED", invalid_callback: "INVALID", refunded: "REFUNDED" };

function StatCard({ label: l, value, sub, testId }) {
  return (
    <div className="border border-[#2e2e2a] p-4 lg:p-6" data-testid={testId}>
      <p className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a]">{l}</p>
      <p className="mt-2 font-display font-extrabold text-3xl lg:text-4xl">{value}</p>
      {sub && <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-[#a3a39f]">{sub}</p>}
    </div>
  );
}

function F({ k, v }) {
  return (
    <div className="min-w-0">
      <p className="font-mono text-[9px] tracking-[0.2em] text-[#6e6e6a]">{k}</p>
      <p className="mt-0.5 text-sm text-[#f0eee4] break-words">{v || v === 0 ? v : "—"}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-[#2e2e2a] pt-4">
      <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-3">{title}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">{children}</div>
    </div>
  );
}

export default function AdminBookings({ token }) {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [ev, setEv] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", category_id: "", addon: "", date_from: "", date_to: "" });
  const [qInput, setQInput] = useState("");
  const [sel, setSel] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [busy, setBusy] = useState(false);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [filters]);

  const load = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers: { "X-Admin-Token": token } }).then((r) => r.json()),
        fetch(`${API}/api/admin/bookings?${qs()}`, { headers: { "X-Admin-Token": token } }).then((r) => r.json()),
      ]);
      setStats(s);
      setRows(b.bookings || []);
    } catch {
      toast.error("Could not load bookings");
    }
  }, [token, qs]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API}/api/event`).then((r) => r.json()).then(setEv).catch(() => {});
  }, []);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => { setFilters({ q: "", status: "", category_id: "", addon: "", date_from: "", date_to: "" }); setQInput(""); };

  const download = async (path, filename) => {
    const res = await fetch(`${API}/api/admin/${path}`, { headers: { "X-Admin-Token": token } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const markPaid = async (id) => {
    await fetch(`${API}/api/admin/bookings/${id}/mark-paid`, { method: "POST", headers: { "X-Admin-Token": token } });
    toast.success("Marked as paid");
    setSel(null);
    load();
  };

  const recordRefund = async () => {
    const paise = Math.round(Number(refundAmt) * 100);
    if (!paise || paise <= 0) { toast.error("Enter a refund amount in ₹"); return; }
    setBusy(true);
    const res = await fetch(`${API}/api/admin/bookings/${sel.id}/refund`, {
      method: "POST",
      headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
      body: JSON.stringify({ amount_paise: paise, reason: refundReason }),
    });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { toast.error(data.detail || "Refund failed"); return; }
    toast.success("Refund recorded — booking marked REFUNDED");
    setSel(null);
    setRefundAmt("");
    setRefundReason("");
    load();
  };

  const st = stats?.by_status || {};
  const pendingCount = st.hold || 0;
  const failedCount = (st.failed || 0) + (st.invalid_callback || 0);

  const timeline = (b) => {
    if (b.history?.length) return b.history;
    const t = [{ at: b.created_at, event: "created", note: "" }];
    if (b.paid_at) t.push({ at: b.paid_at, event: "paid", note: "" });
    if (b.refund?.at) t.push({ at: b.refund.at, event: "refunded", note: b.refund.reason });
    (b.tickets || []).filter((x) => x.checked_in).forEach((x) => t.push({ at: x.checked_in_at, event: "checkin", note: x.name }));
    return t;
  };

  return (
    <div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-[#2e2e2a] border border-[#2e2e2a]" data-testid="admin-stats">
          <div className="bg-[#161614]"><StatCard label="TOTAL" value={stats.total} testId="stat-total" /></div>
          <div className="bg-[#161614]"><StatCard label="PAID" value={st.paid || 0} testId="stat-paid" /></div>
          <div className="bg-[#161614]"><StatCard label="PENDING" value={pendingCount} sub="unpaid holds" testId="stat-pending" /></div>
          <div className="bg-[#161614]"><StatCard label="FAILED" value={failedCount} testId="stat-failed" /></div>
          <div className="bg-[#161614]"><StatCard label="REFUNDED" value={st.refunded || 0} sub={inr(stats.refunds_paise)} testId="stat-refunded" /></div>
          <div className="bg-[#161614]"><StatCard label="NET COLLECTED" value={inr(stats.net_collected_paise)} sub={`gross ${inr(stats.gross_collected_paise)}`} testId="stat-revenue" /></div>
        </div>
      )}

      <div className="mt-8 border border-[#2e2e2a] p-4 space-y-3" data-testid="admin-filters">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <div className="col-span-2">
            <label className={label}>SEARCH (NAME / EMAIL / PHONE / REF / TXN)</label>
            <input
              className="field"
              data-testid="admin-search-input"
              placeholder="Search registrations…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && set("q", qInput)}
            />
          </div>
          <div>
            <label className={label}>PAYMENT STATUS</label>
            <select className="field" data-testid="admin-filter-status" value={filters.status} onChange={(e) => set("status", e.target.value)}>
              <option value="">ALL</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>CATEGORY</label>
            <select className="field" data-testid="admin-filter-category" value={filters.category_id} onChange={(e) => set("category_id", e.target.value)}>
              <option value="">ALL</option>
              {(ev?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>PROTECTION PLAN</label>
            <select className="field" data-testid="admin-filter-addon" value={filters.addon} onChange={(e) => set("addon", e.target.value)}>
              <option value="">ALL</option>
              <option value="standard">STANDARD</option>
              <option value="lite_flex">LITE FLEX</option>
              <option value="full_flex">FULL FLEX</option>
            </select>
          </div>
          <div>
            <label className={label}>FROM / TO</label>
            <div className="flex gap-2">
              <input type="date" className="field min-w-0 flex-1" data-testid="admin-filter-from" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} />
              <input type="date" className="field min-w-0 flex-1" data-testid="admin-filter-to" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => set("q", qInput)} data-testid="admin-search-btn" className="btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:bg-white">SEARCH</button>
          <button onClick={reset} data-testid="admin-filter-reset" className="border border-[#454540] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:border-[#f0eee4] transition-colors">RESET</button>
          <button onClick={() => download(`export.csv?${qs()}`, "torq-registrations.csv")} data-testid="admin-export-csv" className="border border-[#454540] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:border-[#f0eee4] transition-colors">EXPORT CSV</button>
          <button onClick={() => download(`export.xlsx?${qs()}`, "torq-registrations.xlsx")} data-testid="admin-export-xlsx" className="border border-[#454540] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:border-[#f0eee4] transition-colors">EXPORT EXCEL</button>
          <button onClick={() => download("export/entry-list.pdf", "torq-gate-entry-list.pdf")} data-testid="admin-export-pdf" className="border border-[#454540] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:border-[#f0eee4] transition-colors">GATE SHEET PDF</button>
        </div>
      </div>

      {!rows || rows.length === 0 ? (
        <p className="mt-10 font-mono text-[11px] tracking-[0.2em] text-[#6e6e6a]" data-testid="admin-empty">NO REGISTRATIONS MATCH</p>
      ) : (
        <>
          <div className="mt-8 overflow-x-auto border border-[#2e2e2a] hidden md:block" data-testid="admin-table">
            <table className="w-full text-left font-mono text-[11px] tracking-[0.05em]">
              <thead>
                <tr className="text-[#6e6e6a] border-b border-[#2e2e2a]">
                  {["REF", "ATHLETE", "EMAIL", "CATEGORY", "PLAN", "TOTAL", "STATUS", "CREATED", ""].map((c) => (
                    <th key={c} className="px-4 py-3 font-normal whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => setSel(r)} className="border-b border-[#2e2e2a] last:border-0 hover:bg-[#1e1e1c] cursor-pointer" data-testid={`admin-row-${r.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{r.booking_ref || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.first_name} {r.last_name}{r.partner ? ` + ${r.partner.first_name}` : ""}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.category_label}</td>
                    <td className="px-4 py-3 whitespace-nowrap uppercase">{r.addon?.replace("_", " ")}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{inr(r.amounts?.total_paise)}</td>
                    <td className="px-4 py-3 whitespace-nowrap uppercase">{STATUS_LABELS[r.status] || r.status}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#6e6e6a]">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.status !== "paid" && r.status !== "refunded" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markPaid(r.id); }}
                          data-testid={`admin-mark-paid-${r.id}`}
                          className="border border-[#454540] font-mono text-[10px] tracking-[0.15em] px-3 py-1.5 hover:bg-[#f0eee4] hover:text-[#161614] hover:border-[#f0eee4] transition-colors"
                        >
                          MARK PAID
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 space-y-3 md:hidden" data-testid="admin-cards">
            {rows.map((r) => (
              <button key={r.id} onClick={() => setSel(r)} className="w-full text-left border border-[#2e2e2a] p-4 space-y-2" data-testid={`admin-booking-card-${r.id}`}>
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.15em]">
                  <span className="text-[#6e6e6a]">{r.booking_ref || "NO REF YET"}</span>
                  <span className={`px-2 py-1 border ${r.status === "paid" ? "border-[#f0eee4]" : "border-[#454540] text-[#a3a39f]"} uppercase`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </div>
                <p className="font-display font-bold uppercase text-xl leading-tight">
                  {r.first_name} {r.last_name}{r.partner ? ` + ${r.partner.first_name}` : ""}
                </p>
                <p className="font-mono text-[11px] text-[#a3a39f] break-all">{r.email}</p>
                <div className="flex items-center justify-between font-mono text-[11px] tracking-[0.1em]">
                  <span className="text-[#a3a39f]">{r.category_label} · <span className="uppercase">{r.addon?.replace("_", " ")}</span></span>
                  <span className="font-bold">{inr(r.amounts?.total_paise)}</span>
                </div>
                <p className="font-mono text-[10px] text-[#6e6e6a]">{fmt(r.created_at)}</p>
              </button>
            ))}
          </div>
        </>
      )}

      {sel && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setSel(null)} data-testid="admin-detail-overlay">
          <div
            className="bg-[#161614] border border-[#2e2e2a] w-full max-w-3xl max-h-[88vh] overflow-y-auto p-6 lg:p-8 space-y-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-detail"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a]">{sel.booking_ref || "UNPAID — NO REF"} · <span className="uppercase">{STATUS_LABELS[sel.status] || sel.status}</span></p>
                <h3 className="mt-1 font-display font-extrabold uppercase text-2xl lg:text-3xl" data-testid="admin-detail-name">
                  {sel.first_name} {sel.last_name}
                </h3>
              </div>
              <button onClick={() => setSel(null)} data-testid="admin-detail-close" className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center border border-[#454540] hover:border-[#f0eee4]" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            <Section title="ATHLETE">
              <F k="EMAIL" v={sel.email} />
              <F k="MOBILE" v={sel.phone} />
              <F k="DATE OF BIRTH" v={sel.dob} />
              <F k="GENDER" v={sel.gender} />
              <F k="TEAM" v={sel.team_name} />
              <F k="MARKETING OPT-IN" v={sel.marketing_opt_in ? "YES" : "NO"} />
            </Section>

            {sel.partner && (
              <Section title="PARTNER (DOUBLES)">
                <F k="NAME" v={`${sel.partner.first_name} ${sel.partner.last_name}`} />
                <F k="MOBILE" v={sel.partner.phone} />
                <F k="DATE OF BIRTH" v={sel.partner.dob} />
                <F k="GENDER" v={sel.partner.gender} />
              </Section>
            )}

            <Section title="EMERGENCY CONTACT">
              <F k="NAME" v={sel.emergency_contact?.name} />
              <F k="MOBILE" v={sel.emergency_contact?.phone} />
            </Section>

            <Section title="EVENT & TICKET">
              <F k="EVENT" v={sel.event?.title} />
              <F k="CITY" v={sel.event?.city} />
              <F k="EVENT DATE" v={sel.event?.date_text} />
              <F k="CATEGORY" v={sel.category_label} />
              <F k="PROTECTION PLAN" v={sel.addon?.replace("_", " ").toUpperCase()} />
              <F k="PROMO CODE" v={sel.promo_code} />
            </Section>

            <Section title="AMOUNTS">
              <F k="BASE" v={inr(sel.amounts?.base_paise)} />
              <F k="ADD-ON" v={inr(sel.amounts?.addon_paise)} />
              <F k="DISCOUNT" v={sel.amounts?.discount_paise ? `−${inr(sel.amounts.discount_paise)}` : "—"} />
              <F k="SERVICE FEE" v={inr(sel.amounts?.fee_paise)} />
              <F k="GST" v={inr(sel.amounts?.gst_paise)} />
              <F k="TOTAL" v={inr(sel.amounts?.total_paise)} />
            </Section>

            <Section title="PAYMENT">
              <F k="ORDER / TXN ID" v={sel.txnid} />
              <F k="PAYU PAYMENT ID" v={sel.payment_id} />
              <F k="PAID AT" v={fmt(sel.paid_at)} />
              <F k="REGISTERED AT" v={fmt(sel.created_at)} />
              <F k="STATUS" v={(STATUS_LABELS[sel.status] || sel.status || "").toUpperCase()} />
              <F k="NOTE" v={sel.paid_note} />
            </Section>

            {(sel.tickets || []).length > 0 && (
              <Section title="TICKETS">
                {(sel.tickets || []).map((t) => (
                  <F key={t.token} k={t.name?.toUpperCase()} v={t.checked_in ? `CHECKED IN ${fmt(t.checked_in_at)}` : "NOT CHECKED IN"} />
                ))}
              </Section>
            )}

            {sel.refund && (
              <Section title="REFUND">
                <F k="AMOUNT" v={inr(sel.refund.amount_paise)} />
                <F k="REASON" v={sel.refund.reason} />
                <F k="RECORDED AT" v={fmt(sel.refund.at)} />
              </Section>
            )}

            <Section title="CONSENT">
              <F k="TERMS VERSION" v={sel.consent?.version} />
              <F k="ACCEPTED AT" v={fmt(sel.consent?.at)} />
            </Section>

            <div className="border-t border-[#2e2e2a] pt-4">
              <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-3">TIMELINE</p>
              <div className="space-y-2" data-testid="admin-detail-timeline">
                {timeline(sel).map((h, i) => (
                  <div key={i} className="flex items-baseline gap-3 font-mono text-[11px]">
                    <span className="text-[#6e6e6a] whitespace-nowrap">{fmt(h.at)}</span>
                    <span className="uppercase tracking-[0.15em]">{h.event}</span>
                    {h.note && <span className="text-[#a3a39f]">{h.note}</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#2e2e2a] pt-4 flex flex-wrap items-end gap-4">
              {sel.status !== "paid" && sel.status !== "refunded" && (
                <button
                  onClick={() => markPaid(sel.id)}
                  data-testid="admin-detail-mark-paid"
                  className="border border-[#454540] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:bg-[#f0eee4] hover:text-[#161614] hover:border-[#f0eee4] transition-colors"
                >
                  MARK PAID
                </button>
              )}
              {sel.status === "paid" && (
                <>
                  <div>
                    <label className={label}>REFUND AMOUNT ₹</label>
                    <input className="field w-36" data-testid="admin-refund-amount" type="number" min="1" value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} placeholder={`max ${(sel.amounts?.total_paise || 0) / 100}`} />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <label className={label}>REASON / NOTE</label>
                    <input className="field" data-testid="admin-refund-reason" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="e.g. event cancelled" />
                  </div>
                  <button
                    onClick={recordRefund}
                    disabled={busy}
                    data-testid="admin-refund-submit"
                    className="btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:bg-white disabled:opacity-40"
                  >
                    {busy ? "RECORDING…" : "RECORD REFUND"}
                  </button>
                </>
              )}
              <p className="w-full font-mono text-[9px] tracking-[0.1em] text-[#6e6e6a]">
                Refunds are recorded here for the audit trail — pay out the actual amount from your PayU dashboard.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
